import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Avatar,
  Button,
  useTheme,
} from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import UserDetailsModal from "../../components/UserDetailsModal";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import masterService from "../../services/masterService";

// Helper function to clean and validate dropdown data
const cleanDropdownData = (data) => {
  if (!Array.isArray(data)) return [];
  
  // Remove duplicates based on id and ensure proper structure
  const seen = new Set();
  const cleaned = data.filter(item => {
    if (!item || typeof item !== 'object') return false;
    
    const id = item.id || item.value;
    if (!id || seen.has(id)) return false;
    
    seen.add(id);
    
    // Ensure item has both id and name properties
    if (!item.name && item.value) {
      item.name = item.value;
    }
    
    return true;
  });
  
  return cleaned;
};

const RecommendedUserCard = ({
  user,
  handleHeartClick,
  handleInterested,
  handlePurchase,
  hasSubscription = false,
  isLoggedIn = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [detailedUser, setDetailedUser] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  // Function to get city name by city ID
  const getCityName = useCallback((cityId) => {
    if (!cityId || !cities.length) return null;
    
    const city = cities.find(c => 
      c.id === cityId || 
      c.id === String(cityId) || 
      String(c.id) === String(cityId)
    );
    
    return city ? city.name : null;
  }, [cities]);

  // Function to fetch cities data
  const fetchCities = useCallback(async () => {
    if (!user?.state) return;
    
    setLoadingCities(true);
    try {
      const citiesData = await masterService.getCitiesList(user.state);
      setCities(cleanDropdownData(citiesData));
    } catch (error) {
      console.error("Error fetching cities:", error);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  }, [user?.state]);

  // Fetch cities when user state changes
  useEffect(() => {
    if (user?.state) {
      fetchCities();
    } else {
      setCities([]);
    }
  }, [user?.state, fetchCities]);

  const handleOpenModal = async () => {
    // If user is not logged in, redirect to login page
    // if (!isLoggedIn) {
    //   navigate('/login-page');
    //   return;
    // }

    setOpenModal(true);
    setLoadingDetails(true);
    try {
      const userDetails = await masterService.getUserDetails(user.id);
      setDetailedUser({ ...user, ...userDetails });
    } catch (error) {
      console.error("Error fetching user details:", error);
      setDetailedUser(user); // Fallback to basic user info
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setDetailedUser(null);
  };

  return (
    <Card
      sx={{
        borderRadius: 6,
        boxShadow: "0 6px 24px rgba(62,2,23,0.10)",
        minHeight: 400,
        height: 400,
        width: 320,
        maxWidth: "100%",
        background: `linear-gradient(120deg, ${theme.palette.primary.light}22 0%, #fff 100%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "visible",
        transition: "transform 0.3s, box-shadow 0.3s",
        "&:hover": {
          transform: "translateY(-10px) scale(1.03)",
          boxShadow: "0 16px 40px rgba(62,2,23,0.13)",
        },
      }}
    >
      <CardContent
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: 4,
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Avatar
            src={
              user.image
                ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${user.image}`
                : undefined
            }
            sx={{
              width: 100,
              height: 100,
              border: `4px solid ${theme.palette.primary.main}`,
              boxShadow: "0 4px 16px rgba(62,2,23,0.15)",
              background: "#fff",
              zIndex: 1,
            }}
          />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            mb: 1,
            color: theme.palette.primary.dark,
            textAlign: "center",
          }}
        >
          {user.name || `${user.first_name || ""} ${user.last_name || ""}`}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          {user.age && user.age !== 0 && user.age !== null && user.age !== "" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PersonIcon
                sx={{ color: theme.palette.primary.light, fontSize: 20 }}
              />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {user.age} yrs
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PersonIcon
                sx={{ color: theme.palette.primary.light, fontSize: 20 }}
              />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Not specified
              </Typography>
            </Box>
          )}
          
          {user.city && user.city !== 0 && user.city !== null && user.city !== "" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <HomeIcon
                sx={{ color: theme.palette.primary.light, fontSize: 20 }}
              />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {getCityName(user.city) || "Not specified"}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <HomeIcon
                sx={{ color: theme.palette.primary.light, fontSize: 20 }}
              />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                Not specified
              </Typography>
            </Box>
          )}
        </Box>

        {/* Likes + Interests in one row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 4,
            my: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              onClick={() => handleHeartClick(user.id)}
              sx={{ p: 0.5 }}
              title={user.is_liked_by_me ? "Unlike" : "Like"}
            >
              {user.is_liked_by_me ? (
                <FavoriteIcon sx={{ color: "red", fontSize: 28 }} />
              ) : (
                <FavoriteBorderIcon sx={{ color: "red", fontSize: 28 }} />
              )}
            </IconButton>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              {user.liked_count || 0}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              onClick={() => handleInterested(user.id)}
              sx={{ p: 0.5 }}
              title={user.is_interested_by_me ? "Not Interested" : "Interested"}
            >
              {user.is_interested_by_me ? (
                <ThumbUpAltIcon
                  sx={{ color: theme.palette.primary.main, fontSize: 28 }}
                />
              ) : (
                <ThumbUpOutlinedIcon
                  sx={{ color: theme.palette.text.secondary, fontSize: 28 }}
                />
              )}
            </IconButton>
            <Typography
              variant="body1"
              sx={{
                fontWeight: user.is_interested_by_me ? 600 : 400,
                color: user.is_interested_by_me
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
              }}
            >
              Interested
            </Typography>
          </Box>
        </Box>

        {/* Purchase Button or View Details */}

        {hasSubscription ? (
          // For subscribed users, always show View Details
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenModal}
            sx={{
              mt: 2,
              borderRadius: 4,
              px: 4,
              py: 1,
              textTransform: "none",
              fontWeight: "bold",
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              "&:hover": {
                background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.primary.light})`,
                transform: "scale(1.05)",
              },
              transition: "all 0.2s",
            }}
          >
            View Profile Details
          </Button>
        ) : (
          // For non-subscribed users, show purchase flow
          <>
        {!user.is_purchased && (
          <Box mt={2}>
            <Button
              variant="contained"
              color="secondary"
              sx={{
                borderRadius: 4,
                px: 4,
                py: 1,
                textTransform: "none",
                fontWeight: "bold",
              }}
              onClick={() => handlePurchase(user.unique_id)}
            >
              Purchase / ₹200
            </Button>
          </Box>
        )}
        {user.is_purchased && (
          <Button
            variant="text"
            onClick={handleOpenModal}
            sx={{
              mt: 1,
              color: theme.palette.success.main,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                bgcolor: `${theme.palette.success.main}11`,
                transform: "scale(1.05)",
              },
              transition: "all 0.2s",
            }}
          >
            View Details
          </Button>
            )}
          </>
        )}
      </CardContent>

      {/* User Details Modal */}
      <UserDetailsModal
        open={openModal}
        onClose={handleCloseModal}
        user={detailedUser || user}
        theme={theme}
        loading={loadingDetails}
      />
    </Card>
  );
};

export default RecommendedUserCard;
